import {
  EyeOutlined,
  PictureOutlined,
  SearchOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import {
  Button,
  Drawer,
  FloatButton,
  Form,
  Image,
  Input,
  Space,
  Typography,
  message,
} from 'antd';
import React, { useEffect, useRef, useState } from 'react';

import { OpenAPI } from '@/client';
import { appUsersFacade } from '@/facades/appUsers';
import { decryptImageUrl } from '@/utils/imageCrypto';

const { Paragraph, Text } = Typography;

const getAdminToken = async () => {
  if (typeof OpenAPI.TOKEN === 'function') {
    return OpenAPI.TOKEN({} as never);
  }
  return OpenAPI.TOKEN || '';
};

const JsonPreview: React.FC<{ data: unknown }> = ({ data }) => {
  if (!data) {
    return <Text type="secondary">暂无查询结果</Text>;
  }
  return (
    <Paragraph style={{ marginBottom: 0 }}>
      <pre
        style={{
          background: '#0f172a',
          borderRadius: 10,
          color: '#dbeafe',
          fontSize: 12,
          lineHeight: 1.6,
          margin: 0,
          maxHeight: 'calc(100vh - 300px)',
          overflow: 'auto',
          padding: 16,
        }}
      >
        {JSON.stringify(data, null, 2)}
      </pre>
    </Paragraph>
  );
};

const resolvePreviewEnv = () =>
  window.location.hostname.includes('test') ? 'test' : 'prod';

const bridgePreviewToken = (env: 'test' | 'prod', token: string) => {
  // The admin UI and vweb use different storage keys, but share the same origin.
  // Seed vweb's environment-scoped key before opening the preview window.
  localStorage.setItem(`comic10k:web-admin-token:${env}`, token);
};

const GlobalToolbox: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [ip, setIp] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [imageLoading, setImageLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<unknown>();
  const imagePreviewUrlRef = useRef('');

  useEffect(() => {
    return () => {
      if (imagePreviewUrlRef.current) {
        URL.revokeObjectURL(imagePreviewUrlRef.current);
      }
    };
  }, []);

  const clearImagePreview = () => {
    if (imagePreviewUrlRef.current) {
      URL.revokeObjectURL(imagePreviewUrlRef.current);
      imagePreviewUrlRef.current = '';
    }
    setImagePreviewUrl('');
  };

  const decryptImage = async () => {
    const value = imageUrl.trim();
    if (!value) {
      message.warning('请输入图片地址');
      return;
    }
    setImageLoading(true);
    clearImagePreview();
    try {
      const previewUrl = await decryptImageUrl(value);
      imagePreviewUrlRef.current = previewUrl;
      setImagePreviewUrl(previewUrl);
    } catch (error) {
      const messageText =
        error instanceof Error ? error.message : '图片解密失败';
      message.error(messageText);
    } finally {
      setImageLoading(false);
    }
  };

  const clearToolbox = () => {
    setResult(undefined);
    setIp('');
    setImageUrl('');
    clearImagePreview();
  };

  const lookupIp = async () => {
    const value = ip.trim();
    if (!value) {
      message.warning('请输入 IP');
      return;
    }
    setLoading(true);
    try {
      const data = await appUsersFacade.lookupIpInfo(value);
      setResult(data);
    } catch (error) {
      const messageText =
        error instanceof Error
          ? error.message
          : (error as { message?: string })?.message || 'IP 查询失败';
      message.error(messageText);
      setResult(undefined);
    } finally {
      setLoading(false);
    }
  };

  const openVwebPreview = async (mode: 'mobile' | 'desktop') => {
    const token = await getAdminToken();
    const url = new URL('/vweb/', window.location.origin);
    const env = resolvePreviewEnv();
    url.searchParams.set('env', env);
    url.searchParams.set('mode', mode);
    if (token) {
      bridgePreviewToken(env, token);
      url.searchParams.set('admin_token', token);
    }
    const preview = window.open(
      url.toString(),
      `comic10k-vweb-preview-${mode}`,
      mode === 'desktop'
        ? 'popup=yes,width=1440,height=960'
        : 'popup=yes,width=430,height=820',
    );
    if (!preview) {
      message.warning('浏览器阻止了预览窗口');
      return;
    }
    preview.opener = null;
  };

  return (
    <>
      <FloatButton
        description="工具"
        icon={<ToolOutlined />}
        onClick={() => setOpen(true)}
        style={{ right: 28, bottom: 88 }}
      />
      <Drawer
        destroyOnHidden
        extra={
          <Button
            disabled={!result && !ip && !imageUrl && !imagePreviewUrl}
            onClick={clearToolbox}
          >
            清空
          </Button>
        }
        onClose={() => setOpen(false)}
        open={open}
        title="快捷工具"
        width={520}
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <div>
            <Text strong>手机端预览</Text>
            <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>
              打开 /vweb 模拟器，并沿用当前后台环境和登录态。
            </div>
          </div>
          <Space wrap>
            <Button
              icon={<EyeOutlined />}
              onClick={() => void openVwebPreview('mobile')}
              type="primary"
            >
              手机模式
            </Button>
            <Button
              icon={<EyeOutlined />}
              onClick={() => void openVwebPreview('desktop')}
            >
              PC 模式
            </Button>
          </Space>
          <div>
            <Text strong>IP 查询</Text>
            <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>
              调用后台 geoip 查询接口，返回提供商原始 IP 信息。
            </div>
          </div>
          <Form
            layout="vertical"
            onFinish={() => {
              void lookupIp();
            }}
          >
            <Form.Item label="IP 地址" style={{ marginBottom: 12 }}>
              <Input
                allowClear
                autoFocus
                onChange={(event) => setIp(event.target.value)}
                onPressEnter={() => {
                  void lookupIp();
                }}
                placeholder="例如 8.8.8.8"
                value={ip}
              />
            </Form.Item>
            <Button
              htmlType="submit"
              icon={<SearchOutlined />}
              loading={loading}
              type="primary"
            >
              查询
            </Button>
          </Form>
          <JsonPreview data={result} />
          <div>
            <Text strong>图片快捷解密</Text>
            <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>
              输入加密图片地址，使用系统图片解密算法后直接预览。
            </div>
          </div>
          <Form
            layout="vertical"
            onFinish={() => {
              void decryptImage();
            }}
          >
            <Form.Item label="图片地址" style={{ marginBottom: 12 }}>
              <Input
                allowClear
                onChange={(event) => setImageUrl(event.target.value)}
                placeholder="例如 https://static.example.com/iv_1_n001_...bin"
                value={imageUrl}
              />
            </Form.Item>
            <Button
              icon={<PictureOutlined />}
              loading={imageLoading}
              htmlType="submit"
              type="primary"
            >
              解密并预览
            </Button>
          </Form>
          {imagePreviewUrl && (
            <div
              style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                minHeight: 180,
                padding: 12,
                textAlign: 'center',
              }}
            >
              <Image
                alt="解密后的图片"
                preview
                src={imagePreviewUrl}
                style={{
                  maxHeight: 420,
                  maxWidth: '100%',
                  objectFit: 'contain',
                }}
              />
            </div>
          )}
        </Space>
      </Drawer>
    </>
  );
};

export default GlobalToolbox;
